import {
  TERMINAL_TOOL_NAMES,
  TRACK_HISTORY_REQUEST_TOOL_NAME,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import { isExecutedCatalogToolResult } from './HITL/hitl-card.utils';

const EXECUTABLE_HITL_TOOLS = new Set<string>([
  ...TERMINAL_TOOL_NAMES,
  'python-tool',
  'coder-tool',
  'http-tool',
  'dynamic-http-tool',
  'validated-http-tool',
  'http-executor',
  TRACK_HISTORY_REQUEST_TOOL_NAME,
  'attacker-tool',
]);

const HTTP_HITL_TOOLS = new Set([
  'http-tool',
  'dynamic-http-tool',
  'validated-http-tool',
  'http-executor',
]);
const PYTHON_HITL_TOOLS = new Set(['python-tool', 'coder-tool']);

const TERMINAL_HITL_TOOLS = new Set<string>([
  ...TERMINAL_TOOL_NAMES,
  'remote-c2-command',
]);

const PORTAL_OBJECT_HITL_TOOLS = new Set([
  'finding-tool',
  'create-findings',
  'create-finding',
  'update-findings',
  'update-finding',
  'update-finding-description',
  'signal-tool',
  'update-signal-tool',
  'create-signal',
  'create-signals',
  'update-signal',
  'update-signals',
]);

const ASSET_MERGE_HITL_TOOLS = new Set([
  'merge-assets-tool',
  'revert-merge-tool',
]);

function isPortalObjectHitlTool(toolName: string): boolean {
  return PORTAL_OBJECT_HITL_TOOLS.has(toolName.replace(/_/g, '-'));
}

function isAssetMergeHitlTool(toolName: string): boolean {
  return ASSET_MERGE_HITL_TOOLS.has(toolName.replace(/_/g, '-'));
}

function isResolvedToolResultInCatalog({
  toolCallId,
  toolResultsById,
}: {
  toolCallId?: string;
  toolResultsById?: Record<string, ToolCallGenericMessage>;
}): boolean {
  if (!toolCallId || !toolResultsById) {
    return false;
  }

  const toolResult = toolResultsById[toolCallId];

  if (!toolResult) {
    return false;
  }

  return (
    toolResult.status === 'completed' ||
    toolResult.status === 'error' ||
    toolResult.status === 'aborted'
  );
}

export function shouldHideExecutableHitlToolInMessageList({
  toolName,
  status,
  toolCallId,
  hideRequestIfShownInHITL,
  hitlResolvedToolResultInMessages,
  hitlDecisionType,
  toolResultsById,
}: {
  toolName?: string;
  status?: string;
  toolCallId?: string;
  hideRequestIfShownInHITL?: boolean;
  hitlResolvedToolResultInMessages?: boolean;
  hitlDecisionType?: 'approve' | 'reject' | 'edit';
  toolResultsById?: Record<string, ToolCallGenericMessage>;
}): boolean {
  if (!hideRequestIfShownInHITL || !toolName) {
    return false;
  }

  if (isPortalObjectHitlTool(toolName) || isAssetMergeHitlTool(toolName)) {
    return status !== 'error' && status !== 'aborted';
  }

  if (!EXECUTABLE_HITL_TOOLS.has(toolName)) {
    return false;
  }

  if (status === 'started' || status === 'in-progress') {
    return true;
  }

  if (TERMINAL_HITL_TOOLS.has(toolName)) {
    return hitlDecisionType === undefined;
  }

  const isResolved =
    status === 'completed' || status === 'error' || status === 'aborted';

  if (!isResolved) {
    return false;
  }

  if (PYTHON_HITL_TOOLS.has(toolName)) {
    return (
      !hitlResolvedToolResultInMessages &&
      !isExecutedCatalogToolResult(
        toolCallId ? toolResultsById?.[toolCallId] : undefined,
      )
    );
  }

  if (!HTTP_HITL_TOOLS.has(toolName)) {
    return false;
  }

  return (
    !hitlResolvedToolResultInMessages &&
    !isResolvedToolResultInCatalog({ toolCallId, toolResultsById })
  );
}
